// IntelligencePage — the unified KNOWLEDGE surface.
//
// One sidebar entry, one route, three frames: Ask · Live Data · Documents.
// The intelligence layer of the Command Centre — everything the team reads
// from to make decisions lives here. Commitments are NOT a frame in this
// surface; they live tethered to their KPI on /supply and /demand. That
// tether is load-bearing — hypothesis sits next to delivery, you can't
// pretend they're separate problems. The /ask endpoint can RETRIEVE
// commitments as a third corpus alongside docs and live data, but the
// creating/editing of commitments stays where the strategy is questioned.
//
// Tab state lives in the URL (?tab=ask|live|documents) so the address bar
// is shareable and the back button does the right thing. Old /ask and
// /funnel routes redirect here via App.tsx for bookmark compatibility.
//
// Layout: this component owns the page-head; each tab's body renders with
// `hideHeader` so it doesn't paint a duplicate title bar.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { AskTab } from "./AskTab";
import { DocumentsTab } from "./DocumentsTab";
import { LiveDataTab } from "./LiveDataTab";

type TabId = "ask" | "live" | "documents";

interface TabSpec {
  id: TabId;
  label: string;
  meta: string;
}

const TABS: TabSpec[] = [
  { id: "ask", label: "Ask", meta: "RAG over the team library" },
  { id: "live", label: "Live data", meta: "Metabase · key indicators · funnel" },
  { id: "documents", label: "Documents", meta: "Upload · parse · index" },
];

function isTabId(s: string | null): s is TabId {
  return s === "ask" || s === "live" || s === "documents";
}

export function IntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("tab");
  const tab: TabId = isTabId(raw) ? raw : "ask";

  const activeMeta = useMemo(
    () => TABS.find((t) => t.id === tab)?.meta ?? "",
    [tab],
  );

  function selectTab(id: TabId) {
    // Preserve any other params (none today but future-proofs deep-links).
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">KNOWLEDGE · {activeMeta}</div>
          <h1>Knowledge</h1>
        </div>
      </div>

      <div style={tabStrip} role="tablist" aria-label="Knowledge sources">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => selectTab(t.id)}
            style={tabBtn(tab === t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={tabPanel}>
        {tab === "ask" && <AskTab />}
        {tab === "live" && <LiveDataTab />}
        {tab === "documents" && <DocumentsTab />}
      </div>
    </>
  );
}

// ---- styles --------------------------------------------------------------

const tabStrip: React.CSSProperties = {
  display: "flex",
  gap: 4,
  borderBottom: "1px solid var(--edge)",
  marginBottom: 22,
};

function tabBtn(active: boolean): React.CSSProperties {
  return {
    appearance: "none",
    background: "transparent",
    border: "none",
    color: active ? "var(--ink)" : "var(--ink-faint)",
    padding: "10px 18px",
    fontSize: 13.5,
    fontWeight: active ? 600 : 500,
    cursor: "pointer",
    borderBottom: `2px solid ${active ? "var(--purple)" : "transparent"}`,
    marginBottom: -1,
    transition: "color 120ms ease, border-color 120ms ease",
  };
}

const tabPanel: React.CSSProperties = {
  // No vertical padding here — each tab's body already handles its own
  // spacing. We just provide the layout container.
};
